/**
 * Token Manager for Tankef Authentication
 *
 * Security strategy:
 * - Access token: sessionStorage (cleared when browser closes)
 * - Refresh token: sessionStorage by default (no disk persistence)
 * - Auto-refresh: 60 seconds before expiration
 * - Onboarding token: sessionStorage (pre-auth token for registration flow)
 *
 * All token changes are logged for audit and debugging purposes
 */

import { logger } from '@tankef/env';

// Usar las mismas claves que el sistema existente para compatibilidad
const ACCESS_TOKEN_KEY = 'tankef_auth_token'; // Mismo que TOKEN_KEY en index.ts
const REFRESH_TOKEN_KEY = 'tankef_refresh_token'; // Mismo que REFRESH_TOKEN_KEY en index.ts
const EXPIRES_AT_KEY = 'tankef_expires_at';
const SCOPE_KEY = 'tankef_scope';

// Onboarding tokens (pre-authentication)
const ONBOARDING_TOKEN_KEY = 'onboarding_token';
const ONBOARDING_SESSION_ID_KEY = 'onboarding_session_id';
const ONBOARDING_EXPIRES_AT_KEY = 'onboarding_expires_at';

// Margen de seguridad para refresh (60 segundos antes de expirar)
const REFRESH_MARGIN_MS = 60 * 1000;

export const TokenManager = {
  // ============================================================================
  // Access Token (sessionStorage; se limpia al cerrar pestaña)
  // ============================================================================

  /**
   * Obtiene el access token actual
   */
  getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  },

  /**
   * Guarda el access token con su tiempo de expiración. Persiste en
   * sessionStorage Y localStorage el `expiresAt` para que cualquier
   * fallback al token de localStorage (interceptor de axios) pueda
   * verificar su TTL — cierra A6 que permitía tokens infinitos en
   * localStorage cuando la sessionStorage se vaciaba.
   */
  setAccessToken(token: string, expiresIn: number): void {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    const expiresAt = Date.now() + expiresIn * 1000;
    sessionStorage.setItem(EXPIRES_AT_KEY, expiresAt.toString());
    // A6: mirror expiresAt en localStorage para que el fallback del
    // interceptor (que lee localStorage cuando sessionStorage está vacío)
    // pueda detectar que el token caducó.
    localStorage.setItem(EXPIRES_AT_KEY, expiresAt.toString());
    // No loggear timestamp absoluto — minimización de PII en debug logs.
  },

  /**
   * Verifica si el access token está expirado o próximo a expirar.
   * Lee expiresAt de sessionStorage primero, luego cae a localStorage
   * (mismo orden que el lookup del token en el interceptor de axios).
   */
  isAccessTokenExpired(): boolean {
    if (typeof window === 'undefined') return true;
    const expiresAt =
      sessionStorage.getItem(EXPIRES_AT_KEY) || localStorage.getItem(EXPIRES_AT_KEY);
    if (!expiresAt) return true;
    return Date.now() > parseInt(expiresAt, 10) - REFRESH_MARGIN_MS;
  },

  /**
   * Obtiene el tiempo restante del token en segundos
   */
  getTokenTimeRemaining(): number {
    if (typeof window === 'undefined') return 0;
    const expiresAt = sessionStorage.getItem(EXPIRES_AT_KEY);
    if (!expiresAt) return 0;
    const remaining = parseInt(expiresAt, 10) - Date.now();
    return Math.max(0, Math.floor(remaining / 1000));
  },

  // ============================================================================
  // Refresh Token (sessionStorage; no persistente para mayor seguridad)
  // ============================================================================

  /**
   * Obtiene el refresh token
   */
  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Guarda el refresh token
   */
  setRefreshToken(token: string): void {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  /**
   * Verifica si hay un refresh token disponible
   */
  hasRefreshToken(): boolean {
    if (typeof window === 'undefined') return false;
    return !!sessionStorage.getItem(REFRESH_TOKEN_KEY);
  },

  // ============================================================================
  // Scope
  // ============================================================================

  /**
   * Guarda el scope de los permisos
   */
  setScope(scope: string): void {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(SCOPE_KEY, scope);
  },

  /**
   * Obtiene el scope de permisos
   */
  getScope(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(SCOPE_KEY);
  },

  /**
   * Verifica si tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    const scope = this.getScope();
    if (!scope) return false;
    return scope.split(' ').includes(permission);
  },

  // ============================================================================
  // Onboarding Token (sessionStorage; pre-authentication)
  // ============================================================================

  /**
   * Obtiene el onboarding token
   * Usado en el flujo de onboarding antes de que el usuario esté completamente autenticado
   */
  getOnboardingToken(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(ONBOARDING_TOKEN_KEY);
  },

  /**
   * Guarda el onboarding token con su sesión ID y expiración
   * Llamado después de POST /api/v1/onboarding/sessions o resume/confirm
   */
  setOnboardingToken(token: string, sessionId: string, expiresAt?: string): void {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(ONBOARDING_TOKEN_KEY, token);
    sessionStorage.setItem(ONBOARDING_SESSION_ID_KEY, sessionId);
    if (expiresAt) {
      sessionStorage.setItem(ONBOARDING_EXPIRES_AT_KEY, expiresAt);
    }
    logger.debug('[TokenManager] Onboarding token saved', {
      sessionId,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      tokenLength: token.length,
    });
  },

  /**
   * Obtiene el session ID de la sesión de onboarding actual
   */
  getOnboardingSessionId(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(ONBOARDING_SESSION_ID_KEY);
  },

  /**
   * Verifica si el onboarding token está expirado o próximo a expirar.
   *
   * Política: fail-OPEN cuando el formato del timestamp no se entiende.
   * Antes era fail-safe (asumir expirado), pero eso rechazaba tokens
   * válidos cuando el BE enviaba `expiresAt` en formato no-ISO (epoch
   * numeric, etc.) → AUTH-001 silencioso porque getOnboardingAuthHeaders
   * no agregaba el header Authorization.
   *
   * Acepta:
   *   - ISO 8601: "2026-05-13T18:00:00Z" / "2026-05-13T18:00:00-06:00"
   *   - Epoch milliseconds (13 dígitos): "1715625600000"
   *   - Epoch seconds (10 dígitos): "1715625600"
   *   - Si nada se parsea → false (confía en BE; el token real será
   *     validado en el server y si está expirado responderá 401).
   */
  isOnboardingTokenExpired(): boolean {
    if (typeof window === 'undefined') return true;
    const expiresAt = sessionStorage.getItem(ONBOARDING_EXPIRES_AT_KEY);
    if (!expiresAt) return false;

    let expiryMs: number;
    // Numeric string: epoch seconds (10 chars) o milliseconds (13 chars).
    if (/^\d+$/.test(expiresAt)) {
      const numeric = parseInt(expiresAt, 10);
      expiryMs = expiresAt.length <= 10 ? numeric * 1000 : numeric;
    } else {
      expiryMs = Date.parse(expiresAt);
    }

    if (!Number.isFinite(expiryMs) || expiryMs <= 0) return false;
    return Date.now() > expiryMs - REFRESH_MARGIN_MS;
  },

  /**
   * Verifica si existe una sesión de onboarding válida (token + sessionId
   * presentes Y el token no está expirado).
   */
  hasValidOnboardingSession(): boolean {
    const token = this.getOnboardingToken();
    const sessionId = this.getOnboardingSessionId();
    if (!token || !sessionId) return false;
    return !this.isOnboardingTokenExpired();
  },

  /**
   * Limpia los tokens de onboarding (cuando se completa o se reinicia)
   */
  clearOnboardingTokens(): void {
    if (typeof window === 'undefined') return;
    const hadToken = !!sessionStorage.getItem(ONBOARDING_TOKEN_KEY);
    sessionStorage.removeItem(ONBOARDING_TOKEN_KEY);
    sessionStorage.removeItem(ONBOARDING_SESSION_ID_KEY);
    sessionStorage.removeItem(ONBOARDING_EXPIRES_AT_KEY);
    if (hadToken) {
      logger.info('[TokenManager] Onboarding tokens cleared');
    }
  },

  // ============================================================================
  // Session Management
  // ============================================================================

  /**
   * Limpia todos los tokens (logout)
   */
  clearTokens(): void {
    if (typeof window === 'undefined') return;
    const hadToken = !!sessionStorage.getItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(EXPIRES_AT_KEY);
    sessionStorage.removeItem(SCOPE_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    // Also clear legacy token_type key if present
    sessionStorage.removeItem('tankef_token_type');
    // Mantener localStorage sincronizado: el axios interceptor y DashboardLayout
    // leen los tokens vía getStoredToken() (localStorage). Si no se limpian aquí,
    // quedan tokens fantasma que disparan el loop login↔dashboard.
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // A6: también limpiar el mirror de expiresAt en localStorage para que
    // el fallback no use un TTL stale en una sesión posterior.
    localStorage.removeItem(EXPIRES_AT_KEY);
    // G8: también limpiar marcadores de onboarding que sobreviven al token.
    // Un usuario que hace logout y se registra como otra persona en la misma
    // tab heredaba estos marcadores y veía UI/comportamiento inconsistente.
    sessionStorage.removeItem('tkf.registration.personType');
    sessionStorage.removeItem('auth_terms_gate_accepted');
    // Avisar a los Zustand stores que persisten en storage (unifiedSession,
    // catalogsStore) para que vacíen su slice del onboarding.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('tkf:session:logout'));
    }
    if (hadToken) {
      logger.info('[TokenManager] All authentication tokens cleared (logout)');
    }
  },

  /**
   * Verifica si hay una sesión válida
   */
  hasValidSession(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    return !this.isAccessTokenExpired();
  },

  /**
   * Verifica si puede intentar renovar la sesión
   */
  canRefreshSession(): boolean {
    return this.hasRefreshToken();
  },

  /**
   * Guarda todos los tokens de una respuesta de login.
   *
   * Storage policy:
   * - access_token siempre a localStorage (requerido por el interceptor axios).
   * - refresh_token siempre a sessionStorage (sobrevive recarga, muere al cerrar tab).
   *   Con rememberMe=true también se persiste en localStorage (sobrevive cierre del browser).
   *   Con rememberMe=false se limpia localStorage para no dejar residuos de sesiones previas.
   */
  saveTokens(
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    scope: string,
    rememberMe: boolean
  ): void {
    this.setAccessToken(accessToken, expiresIn);
    this.setScope(scope);
    this.setRefreshToken(refreshToken); // always sessionStorage — survives reload
    if (typeof window !== 'undefined') {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      if (rememberMe) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      } else {
        // Limpia residuos de una sesión previa con rememberMe=true.
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    }
  },

  /**
   * Actualiza tokens después de un refresh. Preserva la decisión original de
   * rememberMe: si estaba en localStorage lo mantiene ahí; siempre actualiza sessionStorage.
   */
  updateTokens(accessToken: string, refreshToken: string, expiresIn: number, scope: string): void {
    const hadLocalRefresh =
      typeof window !== 'undefined' && !!localStorage.getItem(REFRESH_TOKEN_KEY);
    this.setAccessToken(accessToken, expiresIn);
    this.setScope(scope);
    this.setRefreshToken(refreshToken); // always update sessionStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      if (hadLocalRefresh) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
    }
  },
};
